import type { ApplyByEmailFormSchemaType } from '@/components/forms/applyByEmail/ApplyByEmailForm';
import type { OpportunityApplyBy } from '@/types/opportunity';
import axios, { type AxiosResponse } from 'axios';
import type { FieldValue, User } from '../user/profile/profileUtils';
import internalAPI, { handleInternalAPIError } from './internalAPI';

type UserProfileFieldGroups = {
  [profileGroupName: string]: {
    [profileFieldName: string]: FieldValue | null;
  };
};

type GetUserApiProfile = {
  id: string;
  email: string;
  givenName: string;
  familyName: string;
  profileCompletion: number;
  profile: UserProfileFieldGroups;
};

type GetUserApiResponse = {
  profile: GetUserApiProfile;
};

type GetUserResult =
  | {
      success: true;
      user: User;
      status: number;
    }
  | {
      success: false;
      user: null;
      status: number;
    };

const mapUserApiProfileToUser = (profile: GetUserApiProfile): User => {
  return {
    id: profile.id,
    email: profile.email,
    givenName: profile.givenName,
    familyName: profile.familyName,
    profileCompletion: profile.profileCompletion,
    fieldGroups: profile.profile
  };
};

/**
 * Updates the user.
 *
 * @param values to update user profile
 * @returns
 */
export const updateUser = async (values: any) => {
  // Attempt to refresh the token
  const response = await internalAPI(true)
    .put('/api/user', {
      ...values
    })
    .catch((error) => {
      return handleInternalAPIError(error);
    });

  if (response.status == 200) {
    return { success: true, status: response.status };
  }
  return { success: false, status: response.status };
};

/**
 * Updates the user with a patch request.
 *
 * @param values to update user profile
 * @returns
 */
export const patchUser = async (values: any) => {
  const response = await internalAPI(true)
    .patch('/api/user', {
      ...values
    })
    .catch((error) => {
      return handleInternalAPIError(error);
    });

  if (response.status == 200) {
    return { success: true, status: response.status, data: response.data };
  } else {
    return { success: false, status: response.status, data: {} };
  }
};

/**
 * Gets user profile details.
 * @returns
 */
export const getUser = async (): Promise<GetUserResult> => {
  // Attempt to refresh the token
  const response = await internalAPI(true)
    .get<GetUserApiResponse>('/api/user', {
      validateStatus: function (status: number) {
        return status == 200 || status == 401;
      }
    })
    .catch((error) => {
      return handleInternalAPIError(error);
    });

  if (response.status == 200 && 'profile' in response.data) {
    return {
      success: true,
      user: mapUserApiProfileToUser(response.data.profile),
      status: response.status
    };
  }

  return { success: false, user: null, status: response.status };
};

/**
 * Delete user.
 * @returns
 */
export const deleteUser = async (values: any) => {
  const response = await internalAPI(true)
    .delete('/api/user', {
      data: {
        values
      }
    })
    .catch((error) => {
      return handleInternalAPIError(error);
    });

  if (response.status == 200) {
    // return response;
    return { success: true, status: response.status };
  }

  return { success: false, status: response.status };
};

/**
 * Get user file.
 * @returns
 */
export const getUserFile = async (id: string) => {
  const response = await internalAPI(true)
    .get('/api/file/' + id)
    .catch((error) => {
      return handleInternalAPIError(error);
    });

  if (response.status == 200) {
    return { success: true, data: response.data.file };
  }

  return { success: true, status: response.status, data: {} };
};

/**
 * Applies a users to an opportunity
 *
 * @param values credentials
 * @return
 */
export const applyByEmail = async (values: ApplyByEmailFormSchemaType, opportunity: OpportunityApplyBy) => {
  if (!values.email) {
    return false;
  }

  const job: Pick<OpportunityApplyBy, 'id' | 'title' | 'url'> = {
    id: opportunity.id,
    title: opportunity.title,
    url: opportunity.url
  };

  const response = await internalAPI(true)
    .post('/api/apply-by-email', {
      job,
      ...values
    })
    .catch((error) => {
      return handleInternalAPIError(error);
    });

  if (response.status == 200) {
    return { success: true, status: response.status, data: response.data };
  }

  return { success: false, status: response.status, data: {} };
};

/**
 * Get auth url for starting oauth connect redirection process
 * @param data
 * @return
 */
export const oauthConnect = async (data: any) => {
  const response: AxiosResponse = await axios.post(
    '/oauth/connect/' + data.provider,
    {
      host: `users.${process.env.GATSBY_TOKEN_HOST}`,
      lastAction: data.lastAction ? data.lastAction : null
    },
    {
      validateStatus: (status) => {
        return status == 200 || status == 401;
      }
    }
  );
  return {
    status: response.status,
    data: response.data
  };
};
